import { Button } from '@factorialco/deprecated-design-system'
import {
  FinanceFileFragment,
  UpdateSalesDocumentTemplateDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { SalesDocumentTemplateForm } from 'modules/finance/activities/salesDocumentTemplate/components/SalesDocumentTemplateForm'
import { SalesDocumentTemplateLoadingModal } from 'modules/finance/activities/salesDocumentTemplate/components/SalesDocumentTemplateLoadingModal'
import { SalesDocumentTemplateNotFoundModal } from 'modules/finance/activities/salesDocumentTemplate/components/SalesDocumentTemplateNotFoundModal'
import { SalesDocumentTemplateFormValues } from 'modules/finance/activities/salesDocumentTemplate/components/types'
import { useSalesDocumentTemplate } from 'modules/finance/activities/salesDocumentTemplate/useSalesDocumentTemplate'

export const SalesDocumentTemplateUpdateModal = ({
  isOpen,
  onClose,
  refetchQuery,
  salesDocumentTemplateId,
}: {
  isOpen: boolean
  onClose: () => void
  refetchQuery?: string
  salesDocumentTemplateId: string
}) => {
  const {
    company: { name: companyName },
  } = useCurrentSession()
  const { salesDocumentTemplate, loading, notFound } = useSalesDocumentTemplate(
    salesDocumentTemplateId,
    isOpen
  )

  const [updateSalesDocumentTemplate, { loading: updating }] = useMutation(
    UpdateSalesDocumentTemplateDocument,
    {
      extractorFn: (data) => data.financeMutations.updateFinanceSalesDocumentTemplate,
      refetchQueries: refetchQuery ? [refetchQuery] : undefined,
    }
  )

  const form = useForm<SalesDocumentTemplateFormValues>({
    enableReinitialize: true,
    defaultValues: {
      name: salesDocumentTemplate?.name || '',
      footer: salesDocumentTemplate?.footer || '',
      logoFile: undefined,
      default: salesDocumentTemplate?.default || false,
      legalEntity: salesDocumentTemplate?.legalEntity || null,
    },
    validateOnChange: true,
    validate: (data) => {
      const errors: FormErrors<SalesDocumentTemplateFormValues> = {}

      if (!data.name) {
        errors.name = i18n.t('common.validations.empty')
      }

      return errors
    },
    onSubmit: async (values) => {
      const result = await updateSalesDocumentTemplate({
        variables: {
          id: salesDocumentTemplateId,
          name: values.name,
          footer: values.footer,
          logoFile: values.logoFile,
          default: values.default,
        },
      })

      const handled = handleResult(result, (parsedErrors) => {
        if (parsedErrors.name || parsedErrors.footer || parsedErrors.logoFile) {
          form.setErrors({
            name: parsedErrors.name,
            footer: parsedErrors.footer,
            logoFile: parsedErrors.logoFile,
          })
        } else {
          app.setFlash({
            type: 'wrong',
            message: parsedErrors.errors ?? i18n.t('errors.generic_error'),
          })
        }
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.sales_document_templates.modal.update_success'),
          time: 2000,
        })
        userClose()
        return { success: true, data: values }
      } else {
        return {
          errors: [],
          success: false,
        }
      }
    },
  })

  const fetchLogoFile = async (logoFileData: FinanceFileFragment) => {
    if (!logoFileData?.url) return undefined

    try {
      const response = await fetch(logoFileData.url)
      const blob = await response.blob()
      return new File([blob], logoFileData.filename, { type: logoFileData.contentType })
    } catch (errorLogo) {
      return undefined
    }
  }

  useUnsafeEffect(() => {
    if (salesDocumentTemplate?.logoFile) {
      fetchLogoFile(salesDocumentTemplate.logoFile).then((file) => {
        form.setFieldValue('logoFile', file)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesDocumentTemplate?.logoFile])

  const userClose = () => {
    form.resetForm()
    onClose()
  }

  if (!isOpen) return null

  if (loading) {
    return (
      <SalesDocumentTemplateLoadingModal
        title={i18n.t('finance.sales_document_templates.modal.edit_template.title')}
        onClose={onClose}
      />
    )
  }

  if (notFound) {
    return (
      <SalesDocumentTemplateNotFoundModal
        title={i18n.t('finance.sales_document_templates.modal.edit_template.title')}
        salesDocumentTemplateId={salesDocumentTemplateId}
        onClose={onClose}
      />
    )
  }

  if (!salesDocumentTemplate) {
    return null
  }

  return (
    <Modal
      illustration="template"
      title={i18n.t('finance.sales_document_templates.modal.edit_template.title')}
      description={i18n.t('finance.sales_document_templates.modal.edit_template.description')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button type="secondary" onClick={userClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit disabled={updating}>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <SalesDocumentTemplateForm form={form} companyName={companyName} isUpdate />
    </Modal>
  )
}
